/*=========================================================================================
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
          path => router path
          name => router name
          component(lazy loading) => component to load
          meta : {
            rule => which user can have access (ACL)
            breadcrumb => Add breadcrumb to specific page
            pageTitle => Display title besides breadcrumb
          }
==========================================================================================*/

import Vue from 'vue';
import VueAnalytics from 'vue-ua';
import Router from 'vue-router';

import store from '@/store/store.js';
Vue.use(Router)


const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      component: () => import('./layouts/Main.vue'),
      children: [
        {
          path: '/', redirect: 'sales'
        },
        {
          path: 'stats', name: 'stats',
          component: () => import('./views/Stats.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Estadísticas',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Estadísticas', active: true }, ],
          },
        },
        {
          path: 'products', name: 'products',
          component: () => import('./views/Products.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Productos',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Productos', active: true }, ],
          },
        },
        {
          path: 'sales', name: 'sales',
          component: () => import('./views/Sales.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Ventas',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Ventas', active: true }, ],
          },
        },
        {
          path: 'purchases', name: 'purchases',
          component: () => import('./views/Purchases.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Compras',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Compras', active: true }, ],
          },
        },
        {
          path: 'config', name: 'config',
          component: () => import('./views/Config.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Configuración',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Configuración', active: true }, ],
          },
        },
        {
          path: 'newPurchase', name: 'newPurchase',
          component: () => import('./views/NewPurchase.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Nueva compra',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Nueva compra', active: true }, ],
          },
        },
        {
          component: () => import('./views/Categories.vue'),
          path: 'categories', name: 'categories',
          meta: {
            rule: 'editor', pageTitle: 'Categorías',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Categorías', active: true }, ],
          },
        },
        {
          path: 'brands', name: 'brands',
          component: () => import('./views/Brands.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Marcas',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Marcas', active: true }, ],
          },
        },
        {
          path: 'suppliers', name: 'suppliers',
          component: () => import('./views/Suppliers.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Proveedores',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Proveedores', active: true }, ],
          },
        },
        {
          path: 'accounting', name: 'accounting',
          component: () => import('./views/Accounting.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Contabilidad',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Contabilidad', active: true }, ],
          },
        },
        {
          path: 'buyers', name: 'buyers',
          component: () => import('./views/Buyers.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Clientes',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Clientes', active: true }, ],
          },
        },
        {
          path: 'messages', name: 'messages',
          component: () => import('./views/Messages.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Mensajes',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Mensajes', active: true }, ],
          },
        },
        {
          path: 'stockMovements', name: 'stockMovements',
          component: () => import('./views/StockMovements.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Movimientos de stock',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Movimientos de stock', active: true }, ],
          },
        },
        {
          path: 'saleMistakes', name: 'saleMistakes',
          component: () => import('./views/SaleMistakes.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Errores en las ventas',
            breadcrumb: [ { title: 'Home', url: '/' }, { title: 'Ventas', url: '/sales' }, { title: 'Errores en las ventas', active: true }, ],
          },
        },
      ], // children
    }, // path ''
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '/pages',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: 'login',
          name: 'page-login',
          component: () => import('@/views/pages/Login.vue'),
          meta: {
            rule: 'editor', pageTitle: 'Ingresar'
          }
        },
        // Redirect to 404 page, if no match found
        {
          path: '*',
          redirect: 'error-404'
        },
      ] // children
    }, // path 'pages'
  ] // routes
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
      appLoading.style.display = "none";
    }
})

function goToLogin(to) {
  router.push({ path: '/pages/login', query: { to: to.path } })
}

let permissionsByRole = [
  // 0 Nada
  [],
  // 1 Administrador
  [
    'stats',
    'products',
    'sales',
    'purchases',
    'config',
    'newPurchase',
    'categories',
    'brands',
    'suppliers',
    'accounting',
    'buyers',
    'messages',
    'stockMovements',
    'saleMistakes',
    // 'page-login',
  ],
  // 2 Gestión
  ['sales', 'products', 'stockMovements', 'saleMistakes', ],
  // 3 Ventas
  ['sales',],
];


router.beforeEach((to, from, next) => {
  // no controled paths
  if (
    to.name === "page-login" ||
    to.name === "page-forgot-password" ||
    to.name === "page-error-404" ||
    to.name === "page-error-500" ||
    to.name === "page-register" ||
    to.name === "page-comingsoon"
  ) {
    return next();
  }
  // control access
  let vm = new Vue({});
  let index = vm.dataDB.employees.findIndex(i => i.id == localStorage.userID);
  let User = vm.dataDB.employees[index];
  if (index == -1) goToLogin(to);
  // let User = JSON.parse(localStorage.User);
  for (let permittedPage of permissionsByRole[User.roleID]) {
    if (permittedPage == to.name) {
      return next();
    }
  }
  router.push('/');
});

router.afterEach((to, from) => {
  let defaultTitle = 'Admin Rapisuper';
  document.title = `${to.meta.pageTitle} - ${defaultTitle}` || defaultTitle;
});




// - - - - - - - - - - Analytics - - - - - - - - - - //

Vue.use(VueAnalytics, {
  // [Required] The name of your app as specified in Google Analytics.
  appName: 'Rapisuper',
  // [Required] The version of your app.
  appVersion: '1.0',
  // [Required] Your Google Analytics tracking ID.
  trackingId: '<your_tracking_id>',
  // If you're using vue-router, pass the router instance here.
  vueRouter: router,

  // // Global Dimensions and Metrics can optionally be specified.
  // globalDimensions: [
  //   { dimension: 1, value: 'FirstDimension' },
  //   { dimension: 2, value: 'SecondDimension' }
  //   // Because websites are only 2D, obviously. WebGL? What's that?
  // ],

  // globalMetrics: [
  //   { metric: 1, value: 'MyMetricValue' },
  //   { metric: 2, value: 'AnotherMetricValue' }
  // ]
});


export default router
